import React from "react";
import { useTranslation } from "react-i18next";
import { LimitsStatus, LimitsStatusPeriods, LimitsStatusStructure } from "src/api";
import { formatNumberToLocale } from "src/utils";
import "./payment-limits.scss";

interface Props {
  limitsStatus: LimitsStatus | null;
  /** Tightest remaining headroom; used for submit enforcement upstream. */
  availableLimit: number;
  /** Wallet/notion currency the values are denominated in (e.g. USDT). */
  currency: string;
  isLoading?: boolean;
}

/**
 * `defaultValue` keeps the block readable until the keys land on the
 * translation platform — without it a missing key renders as the raw key,
 * and `fallbackLng` cannot help when no language defines it.
 */
const PERIODS: { key: "daily" | "weekly" | "monthly"; labelKey: string; label: string }[] = [
  { key: "daily", labelKey: "PAYMENT_LIMITS.DAILY", label: "Daily" },
  { key: "weekly", labelKey: "PAYMENT_LIMITS.WEEKLY", label: "Weekly" },
  { key: "monthly", labelKey: "PAYMENT_LIMITS.MONTHLY", label: "Monthly" },
];

/**
 * Always-expanded limit summary: a static "Available limits" title with the
 * per-period used/limit rows below it. All values are in the wallet currency.
 * A period is shown only when a cap is configured; the block hides entirely
 * when nothing is set.
 *
 * Withdrawals can also carry an `aggregated` (cross-currency) cap. It feeds
 * `availableLimit` upstream, so it gets its own section — otherwise submit
 * could block while every row on screen still looks well within range.
 */
const PaymentLimits: React.FC<Props> = ({ limitsStatus, currency, isLoading = false }) => {
  const { t } = useTranslation();

  const buildRows = (periods: LimitsStatusPeriods | null | undefined, keyPrefix: string) =>
    PERIODS.map(({ key, labelKey, label }) => {
      const bucket: LimitsStatusStructure | undefined = periods?.[key];
      if (!bucket || bucket.limit == null) return null;
      const used = bucket.filled ?? 0;
      return (
        <div className="payment-limits__row" key={`${keyPrefix}-${key}`}>
          <span className="payment-limits__row-label">{t(labelKey, { defaultValue: label })}</span>
          <span className="payment-limits__row-value">
            {`${formatNumberToLocale(used, 2)} / ${formatNumberToLocale(bucket.limit, 2)} ${currency}`}
          </span>
        </div>
      );
    }).filter(Boolean);

  const paymentRows = buildRows(limitsStatus?.payments, "payments");
  const aggregatedRows = buildRows(limitsStatus?.aggregated, "aggregated");

  if (!isLoading && paymentRows.length === 0 && aggregatedRows.length === 0) return null;

  return (
    <div className="payment-limits">
      <span className="payment-limits__title">
        {t("PAYMENT_LIMITS.AVAILABLE", { defaultValue: "Available limits" })}
      </span>
      <div className="payment-limits__body">{paymentRows}</div>

      {aggregatedRows.length > 0 && (
        <>
          <span className="payment-limits__subtitle">
            {t("PAYMENT_LIMITS.AGGREGATED", { defaultValue: "Total across all currencies" })}
          </span>
          <div className="payment-limits__body">{aggregatedRows}</div>
        </>
      )}
    </div>
  );
};

export default PaymentLimits;
